import type { TranslationKey } from './fr';

/** Typé sur les clés françaises : une traduction manquante casse la compilation. */
export const en: Record<TranslationKey, string> = {
  'app.title': 'Paragon Studio',
  'app.subtitle': 'Licensing',

  'nav.servers': 'My server',
  'nav.licenses': 'My licences',
  'nav.admin': 'Back office',
  'nav.logout': 'Sign out',
  'nav.language': 'Français',

  'login.title': 'Sign in',
  'login.intro': 'Sign in with Discord to manage your server and licences.',
  'login.button': 'Sign in with Discord',
  'login.failed': 'Sign-in failed.',

  'common.loading': 'Loading…',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.never': 'never',
  'common.none': 'none',
  'common.retry': 'Retry',
  'common.search': 'Search',
  'common.empty': 'Nothing to show.',

  'secret.shownOnce': 'Shown once',
  'secret.warning':
    'This value will never be shown again — we only keep a fingerprint of it. Copy it now.',
  'secret.acknowledge': 'I have copied it',

  'servers.title': 'My server',
  'servers.none': 'You do not have a server yet.',
  'servers.create': 'Create a server',
  'servers.nameLabel': 'Network name',
  'servers.namePlaceholder': 'My UHC network',
  'servers.quotaReached':
    'You have reached your server quota. Contact support if you genuinely run several networks.',
  'servers.role': 'Role',
  'servers.members': 'Members',
  'servers.tokenCreated':
    'Server created. Here is the pairing token to paste into the plugin configuration.',

  'server.token': 'Pairing token',
  'server.tokenHint':
    'One token for the whole network: every Spigot node uses the same one, even behind a proxy.',
  'server.tokenPrefix': 'Prefix',
  'server.tokenCreated': 'Created on',
  'server.tokenLastUsed': 'Last used',
  'server.tokenNever': 'never used',
  'server.tokenRotate': 'Regenerate',
  'server.tokenRevoke': 'Revoke',
  'server.tokenGrace': 'Keep the previous token valid for',
  'server.tokenGraceMinutes': 'minutes',
  'server.tokenGraceHint':
    'Time to update your config.yml files without downtime. 0 for an immediate cut-off.',
  'server.tokenRotated': 'New token. The previous one stays accepted until {until}.',
  'server.tokenRotatedNow': 'New token. The previous one is revoked immediately.',
  'server.tokenRevokeConfirm':
    'Revoking the token will cut the plugin off at the next lease renewal. Continue?',
  'server.tokenGraceActive': 'Previous token, still accepted until {until}',

  'server.modules': 'Modules active on this server',
  'server.modulesEmpty': 'No module activated. Activate a licence from “My licences”.',

  'server.team': 'Team',
  'server.teamHint':
    'Each member activates their own licences on this server. If they leave, their modules switch off.',
  'server.invite': 'Invite',
  'server.inviteCreated': 'Invitation code created.',
  'server.inviteRole': 'Role',
  'server.inviteUses': 'Uses',
  'server.inviteTtl': 'Validity (hours)',
  'server.inviteRevoke': 'Revoke invitation',
  'server.invites': 'Pending invitations',
  'server.memberRemove': 'Remove',
  'server.memberLeave': 'Leave the team',
  'server.memberRemoveConfirm':
    'Removing this member releases their active licences on this server, with a 24-hour delay before they can reactivate elsewhere. Continue?',
  'server.memberLeaveConfirm':
    'Leaving the team releases your active licences on this server, with a 24-hour delay before you can reactivate them elsewhere. Continue?',
  'server.transferOwnership': 'Transfer ownership',
  'server.transferConfirm':
    'You will become an administrator and will no longer be able to delete the server. Continue?',
  'server.delete': 'Delete the server',
  'server.deleteConfirm':
    'Deleting releases the whole team’s licences, with no delay for them, and revokes the token. Continue?',
  'server.joinTitle': 'Join a team',
  'server.joinCode': 'Invitation code',
  'server.join': 'Join',

  'server.instances': 'Observed instances',
  'server.instancesHint':
    'What the API actually sees of your servers. Several instances are normal on a multi-node network.',
  'server.instanceVersion': 'Version',
  'server.instanceIp': 'Observed IP',
  'server.instanceDeclared': 'Declared IP',
  'server.instanceLastSeen': 'Last seen',
  'server.instanceNone': 'No instance has reported yet.',
  'server.instanceWatched': 'Under watch',

  'licenses.title': 'My licences',
  'licenses.none': 'You have no licence yet.',
  'licenses.allAccess': 'All access',
  'licenses.perpetual': 'One-off purchase',
  'licenses.subscription': 'Subscription',
  'licenses.activeOn': 'Active on {server}',
  'licenses.inactive': 'Not activated',
  'licenses.activate': 'Activate',
  'licenses.transfer': 'Transfer',
  'licenses.release': 'Release',
  'licenses.releaseConfirm':
    'Releasing this licence removes it from the server, and a 24-hour delay applies before you can reactivate it. Continue?',
  'licenses.chooseServer': 'Choose a server',
  'licenses.noServer': 'Create or join a server first.',
  'licenses.cooldown': 'Movable in {duration}',
  'licenses.cooldownHint':
    'This delay stops a licence from hopping between servers. Support can lift it.',
  'licenses.expiresOn': 'Expires on {date}',
  'licenses.transferCount': '{count} transfer(s)',
  'licenses.statusEXPIRED': 'Expired',
  'licenses.statusGRACE': 'Payment grace period',
  'licenses.statusREVOKED': 'Revoked',
  'licenses.statusREFUNDED': 'Refunded',
  'licenses.statusPENDING': 'Pending',
  'licenses.statusACTIVE': 'Active',

  'admin.title': 'Back office',
  'admin.accounts': 'Accounts',
  'admin.signals': 'Monitoring',
  'admin.modules': 'Catalogue',
  'admin.searchPlaceholder': 'Discord ID, username or email',
  'admin.quota': 'Server quota',
  'admin.quotaUpdate': 'Change quota',
  'admin.grant': 'Grant a licence',
  'admin.grantKind': 'Kind',
  'admin.grantModule': 'Module',
  'admin.grantPeriodEnd': 'Period end',
  'admin.grantNote': 'Internal note',
  'admin.revoke': 'Revoke',
  'admin.refund': 'Refund',
  'admin.clearCooldown': 'Lift the delay',
  'admin.reason': 'Reason',
  'admin.ownedServers': 'Owned servers',
  'admin.entitlements': 'Licences',
  'admin.signalsEmpty': 'No open signal.',
  'admin.signalResolve': 'Mark as handled',
  'admin.signalOccurrences': '{count} occurrence(s)',
  'admin.moduleKey': 'Key',
  'admin.moduleKeyHint':
    'Frozen for life: it travels inside signed leases and the plugin compares it verbatim.',
  'admin.moduleName': 'Display name',
  'admin.moduleStatus': 'Status',
  'admin.moduleAllAccess': 'Included in all access',
  'admin.moduleCreate': 'Create a module',
  'admin.expiryJob': 'Process expired subscriptions',

  'error.title': 'Error',
  'error.network': 'The API is unreachable. Check your connection and try again.',
  'error.unauthenticated': 'Your session has expired. Please sign in again.',
  'error.forbidden': 'You do not have the rights for this action.',
  'error.insufficient_role': 'Your role on this server does not allow this action.',
  'error.not_found': 'Not found.',
  'error.conflict': 'Not possible in the current state.',
  'error.invalid_payload': 'The submitted information is invalid.',
  'error.rate_limited': 'Too many requests. Please wait a moment.',
  'error.internal_error': 'An internal error occurred. Please try again later.',
  'error.server_quota_reached':
    'You have reached your server quota. Support can raise it if you run several networks.',
  'error.member_limit_reached': 'This team has reached its maximum number of members.',
  'error.invite_limit_reached': 'Too many pending invitations. Revoke one first.',
  'error.invalid_invite': 'This invitation code is invalid, expired or already used.',
  'error.already_active': 'This licence is already active on a server.',
  'error.already_on_server': 'This licence is already on that server.',
  'error.not_active': 'This licence is not active on any server.',
  'error.cooldown_active': 'This licence is still within its move delay.',
  'error.entitlement_not_active': 'This licence can no longer be used.',
  'error.owner_must_transfer_first': 'The owner cannot be removed: transfer ownership first.',
  'error.owner_role_immutable': 'The owner’s role cannot be changed.',
  'error.quota_below_existing':
    'The quota cannot be lower than the number of servers already owned.',
  'error.module_key_taken': 'That module key is already taken.',
  'error.csrf_failed': 'Request refused. Reload the page and try again.',
  'error.oauth_unconfigured': 'Discord sign-in is not configured on this server.',
  'error.access_denied': 'You declined the Discord authorisation.',
  'error.state_mismatch': 'The sign-in attempt expired. Please try again.',
  'error.account_suspended': 'This account is suspended.',

  'error.private_network.title': 'Non-routable network address',
  'error.private_network.body':
    'The API sees your server connecting from a private address (127.x, 10.x, 192.168.x…). ' +
    'Two likely causes: you are testing the plugin locally, or the reverse proxy in front of ' +
    'the API is not forwarding the real address. Use the plugin’s diagnostic command to see ' +
    'the observed address, and contact support if you are testing locally.',
};
